import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDownRight } from "lucide-react";

// Cap rotation sequence — uses transform rotateY tied to scroll.
// The "surprise" smiling-person reveal slides in as the cap rotates past 45°.

const CAP_IMG =
  "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=1200&auto=format&fit=crop&q=85";
// Alternate image shown while the cap rotates past 45°
const CAP_IMG_BACK = "/assets/images/embroidery-macro.jpeg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Smooth scroll progress
  const smooth = useSpring(scrollYProgress, { stiffness: 60, damping: 20, mass: 0.5 });

  // Cap rotates 360°, scales subtly as user scrolls
  const capRotate = useTransform(smooth, [0, 1], [0, 360]);
  const capScale = useTransform(smooth, [0, 0.5, 1], [1, 0.95, 0.8]);
  const capOpacity = useTransform(smooth, [0, 0.85, 1], [1, 1, 0]);

  // Headline split lines drift
  const titleY = useTransform(smooth, [0, 1], ["0%", "-40%"]);
  const titleOpacity = useTransform(smooth, [0, 0.6], [1, 0.05]);

  const eyebrowOpacity = useTransform(smooth, [0, 0.3], [1, 0]);

  // Reveal caption
  const revealOpacity = useTransform(smooth, [0.55, 0.85], [0, 1]);
  const revealY = useTransform(smooth, [0.55, 0.85], [30, 0]);

  // Track whether rotation passed half a turn so we can swap the image
  const [showAlt, setShowAlt] = useState(false);

  useEffect(() => {
    const unsubscribe = capRotate.onChange((v) => {
      // normalize to 0-360 then check >= 45
      const norm = ((v % 360) + 360) % 360;
      setShowAlt(norm >= 45);
    });
    return () => unsubscribe && unsubscribe();
  }, [capRotate]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[300vh]"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#F7F5F0]">
        {/* Faint grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        {/* Eyebrow line */}
        <motion.div
          style={{ opacity: eyebrowOpacity }}
          className="absolute top-24 md:top-28 left-6 md:left-12 z-20 flex items-center gap-3"
        >
          <span className="w-10 h-px bg-black" />
          <span className="label-eyebrow">FW · MMXXVI · Vol. 01</span>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          style={{ opacity: eyebrowOpacity }}
          className="absolute top-24 md:top-28 right-6 md:right-12 z-20 flex items-center gap-2 label-eyebrow"
        >
          Scroll to inspect <ArrowDownRight size={14} />
        </motion.div>

        {/* Headline */}
        <motion.div
          style={{ y: titleY, opacity: titleOpacity }}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 pointer-events-none flex flex-col items-center text-center px-4"
        >
          <h1
            data-testid="hero-headline"
            className="font-display text-[18vw] md:text-[14vw] leading-[0.85] tracking-[-0.05em]"
          >
            <span className="block">CROWN</span>
            <span className="block italic font-light text-[#C9A961]">&amp; stitch</span>
          </h1>
        </motion.div>

        {/* Rotating cap */}
        <motion.div
          style={{
            rotate: capRotate,
            scale: capScale,
            opacity: capOpacity,
          }}
          className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
        >
          <motion.img
            key={showAlt ? "alt" : "front"}
            src={showAlt ? CAP_IMG_BACK : CAP_IMG}
            alt="Embroidered cap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.24 }}
            className="w-[58vw] md:w-[40vw] max-w-[520px] aspect-square object-cover"
            style={{
              filter: "drop-shadow(0 30px 40px rgba(0,0,0,0.15))",
              borderRadius: "50%",
            }}
          />
        </motion.div>

        {/* Reveal caption */}
        <motion.div
          style={{ opacity: revealOpacity, y: revealY }}
          className="absolute bottom-12 left-6 md:left-12 z-30 max-w-md"
        >
          <div className="label-eyebrow mb-3">The fit, revealed</div>
          <p className="font-display text-2xl md:text-3xl leading-tight">
            Engineered for the shape of a real head — not a mannequin.
          </p>
          <Link
            to="/shop"
            data-testid="hero-shop-button"
            className="inline-flex items-center gap-2 mt-6 bg-black text-white px-6 py-3 text-sm tracking-wide hover:bg-[#C9A961] transition-colors"
          >
            Enter the shop <ArrowDownRight size={16} />
          </Link>
        </motion.div>

        {/* Bottom ticker */}
        <div className="absolute bottom-0 inset-x-0 border-t border-black/10 bg-white/40 backdrop-blur overflow-hidden">
          <div className="flex marquee whitespace-nowrap py-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex shrink-0 gap-12 pr-12 label-eyebrow">
                <span>· Hand-finished</span>
                <span>· 24-stitch crown</span>
                <span>· Numbered editions</span>
                <span>· Kids &amp; Adults</span>
                <span>· Made in small batches</span>
                {/* <span>· Free returns</span> */}
                <span>· Hand-finished</span>
                <span>· 24-stitch crown</span>
                <span>· Numbered editions</span>
                <span>· Kids &amp; Adults</span>
                <span>· Made in small batches</span>
                {/* <span>· Free returns</span> */}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
